const React = require("react");
const { Link, useLocation } = require("react-router-dom");
const { MdVisibility, MdAddShoppingCart, MdSync, MdDeleteForever } = require("react-icons/md");
const { useCJDropshipping } = require("../context/CJDropshippingContext");
const { tokenizeJSON } = require("./jsonHighlight");
// Même logo que Header.js/headerShopSeller.js (logoelakayDiamant-256.png) --
// pas elakayLogo.jsx (base64 PNG de 400+ Ko, bien trop lourd pour une icône
// de card, et de toute façon plus l'image de marque actuelle). Import
// cross-frontière identique au pattern déjà en place ailleurs dans ce module
// (ex: allPriceFeet.jsx, usa.json) -- ce dossier est bundlé par le même
// webpack CRA que front_elakay/src, donc le fichier est émis une seule fois
// (même URL/cache que les autres imports de ce logo), pas une copie par usage.
//
// Surtout PAS logoelakayDiamant.svg : ce fichier est un PNG encodé en base64
// dans un SVG, et le loader SVG de react-scripts (@svgr/webpack) recopie tout
// son contenu dans le bundle JS dès qu'un seul fichier JS l'importe.
//
// interopDefault : un require() brut peut renvoyer soit l'URL (images), soit
// l'objet module complet ({default: url, ...}) selon le loader -- sans lui
// <img src={...}> affichait "[object Object]" (icône invisible/cassée). Même
// interop déjà nécessaire ailleurs dans ce module pour react-bootstrap (voir
// CJSyncModal.js/CJDropshippingViewModal.js).
const interopDefault = (mod) => (mod && mod.default) ? mod.default : mod;
const elakayLogoIcon = interopDefault(require("../../../src/includes/Layout/icon/logoelakayDiamant-256.png"));
const h = React.createElement;

// Rendu React du tokenizer pur de jsonHighlight.js -- partagé par le tab
// Console produit et le nouveau tab JSON fournisseur (CJDropshippingViewModal.js).
const HighlightedJSON = ({ data }) => h("pre", { className: "cjds-console-pre" },
  ...tokenizeJSON(JSON.stringify(data, null, 2)).map((token, index) => token.className
    ? h("span", { key: index, className: token.className }, token.text)
    : token.text)
);

// Téléchargement client-side (Blob + <a download>), aucun appel serveur --
// utilisé par les tabs Console produit ET JSON fournisseur.
const downloadJSON = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

// Construit un lien vers le détail produit qui porte l'URL d'origine dans
// `?from=` -- DropshippingProductDetails.js lit ce paramètre pour que "Back
// to products" retourne exactement à la page/filtre/page de pagination
// d'où l'utilisateur venait, plutôt qu'un chemin fixe.
const buildProductDetailsLink = (productId, location) =>
  `/dropshipping/products/${productId}?from=${encodeURIComponent(`${location.pathname}${location.search}`)}`;

const PageHeader = ({ title, description, action }) => h("div", { className: "cjds-head" },
  h("div", null,
    h("h2", null, title),
    description ? h("p", { className: "cjds-muted" }, description) : null
  ),
  action || null
);

const Button = ({ children, variant = "", className = "", type = "button", ...props }) => h(
  "button",
  { type, className: `cjds-btn ${variant} ${className}`.trim(), ...props },
  children
);

const LinkButton = ({ children, to, variant = "", className = "", ...props }) => h(
  Link,
  { className: `cjds-btn ${variant} ${className}`.trim(), to, ...props },
  children
);

const StatusBadge = ({ status, t }) => {
  const normalized = String(status || "unknown").toLowerCase();
  const className = ["error", "inactive", "unavailable", "cancelled", "not_connected"].includes(normalized)
    ? "cjds-badge error"
    : ["pending", "unknown", "processing"].includes(normalized)
      ? "cjds-badge pending"
      : "cjds-badge";
  return h("span", { className }, t(`h_cj_status_${normalized}`, status || "Unknown"));
};

// `emptyTitle`/`emptyDescription` let a caller override just the empty-state
// copy for a specific reason (e.g. ProductCollectionPage.js explaining why
// the Ship From filter found nothing) while reusing this same component --
// both optional, defaulting to the exact original hardcoded keys/fallbacks
// below, so every other existing caller (Suppliers, Orders, Categories,
// Platforms, view-modal tabs, etc.) keeps its current behavior unchanged.
const ResourceState = ({ loading, error, empty, onRetry, t, emptyTitle, emptyDescription, children }) => {
  if (loading) return h("div", { className: "cjds-state", role: "status" },
    h("h3", null, t("h_cj_loading", "Loading…")),
    h("p", { className: "cjds-muted" }, t("h_cj_loading_desc", "CJ Dropshipping data is being retrieved."))
  );
  if (error) return h("div", { className: "cjds-state", role: "alert" },
    h("h3", null, t("h_cj_error_title", "Unable to load data")),
    h("p", { className: "cjds-muted" }, error),
    h(Button, { onClick: onRetry }, t("h_cj_retry", "Retry"))
  );
  if (empty) return h("div", { className: "cjds-state" },
    h("h3", null, emptyTitle || t("h_cj_empty_title", "Nothing here yet")),
    h("p", { className: "cjds-muted" }, emptyDescription || t("h_cj_empty_desc", "Connect CJ or adjust your filters to see data.")),
    onRetry ? h(Button, { variant: "secondary", onClick: onRetry }, t("h_cj_refresh", "Refresh")) : null
  );
  return children;
};

const ViewButton = ({ onView, t }) => h(Button, {
  variant: "secondary",
  className: "cjds-view-btn",
  onClick: onView,
  "aria-label": t("h_cj_view", "View"),
  title: t("h_cj_view", "View"),
}, h(MdVisibility), t("h_cj_view", "View"));

// Icône circulaire colorée, même langage visuel que les action-btn de
// front_elakay/src/includes/pageInside/Card.js (bouton rond, dégradé,
// élévation au survol) -- ici pour les actions de card produit CJ.
const IconAction = ({ icon, onClick, disabled, title, className = "", href, to }) => {
  const props = {
    className: `cjds-icon-btn ${className}`.trim(),
    onClick: (event) => { event.preventDefault(); event.stopPropagation(); onClick?.(); },
    disabled,
    title,
    "aria-label": title,
  };
  if (to) return h(Link, { ...props, to, onClick: (event) => { event.stopPropagation(); onClick?.(event); } }, icon);
  if (href) return h("a", { ...props, href, onClick: undefined }, icon);
  return h("button", { type: "button", ...props }, icon);
};

// Prix affiché comme Card.js/ProductsPrice.js (prix courant + prix barré +
// badge pourcentage), mais réadapté au contexte CJ : il n'existe pas encore
// de "remise" réelle pour un produit pas encore importé -- le badge montre
// donc la MARGE (coût CJ -> prix de vente suggéré), en vert plutôt qu'en
// rouge, pour rester honnête sur ce qu'il représente.
//
// Deux lignes désormais, chacune avec sa propre icône pour lever toute
// ambiguïté sur QUEL prix est affiché : le logo Elakay pour le prix de vente
// suggéré (ce que le produit affichera sur Elakay), le logo/badge de la
// plateforme (product.platformId.logo, ex. CJ Dropshipping -- déjà populate
// par listProducts côté serveur) pour le coût fournisseur. Pas de logo CJ
// officiel embarqué dans ce repo (question de licence/marque) -- repli sur
// un badge texte "CJ" si platformId.logo n'est pas configuré côté admin.
// Switch Elakay/CJ : bascule l'affichage entre le prix de vente Elakay
// (suggestedSalePrice, marge incluse) et le coût réel CJ (costPrice +
// shippingPrice du fournisseur) -- deux prix différents qu'on ne peut pas
// tous les deux afficher en avant sans confondre le vendeur sur "combien je
// vends" vs "combien ça me coûte livré". État local (pas de persistance
// nécessaire, purement une préférence d'affichage par carte).
const ProductCardPrice = ({ product, t }) => {
  const [source, setSource] = React.useState("elakay");
  const cost = Number(product.costPrice) || 0;
  const suggested = Number(product.suggestedSalePrice) || 0;
  const shipping = Number(product.shippingPrice) || 0;
  const currency = product.currency || "USD";
  const hasMargin = cost > 0 && suggested > cost;
  const marginPercent = hasMargin ? Math.round(((suggested - cost) / cost) * 100) : 0;
  const money = (value) => `${value.toFixed(2)} ${currency}`;
  const platformLogo = product.platformId?.logo;
  const platformName = product.platformId?.name || product.supplierName || "CJ";
  const platformBadgeText = platformName.replace(/dropshipping/i, "").trim().slice(0, 2).toUpperCase() || "CJ";
  // Une fois importé, le vrai prix de vente vit sur le Product Elakay --
  // exposé séparément sous `elakayPricing` (voir catalog.controller.js
  // listProducts) plutôt que peuplé directement sur `elakayProductId`, car ce
  // dernier reste consommé comme ID brut (string) par compare/sync/delete/
  // pricing ailleurs dans ce module. suggestedSalePrice ci-dessus n'est
  // qu'un instantané CJ figé au moment du cache, pas la vérité vive.
  // `salePrice` sur le Product Elakay est un POURCENTAGE de réduction (pas
  // un montant), même convention que productsPrice.js:newPrice côté vitrine.
  const elakayPricing = product.elakayPricing || null;
  const hasRealElakayPrice = Boolean(elakayPricing) && elakayPricing.price != null;
  const elakayBasePrice = hasRealElakayPrice ? Number(elakayPricing.price) || 0 : 0;
  const elakayDiscountPercent = hasRealElakayPrice ? Number(elakayPricing.salePrice) || 0 : 0;
  const elakayPrice = hasRealElakayPrice
    ? elakayBasePrice * (100 - elakayDiscountPercent) / 100
    : (hasMargin ? suggested : cost);
  // Choix du vendeur à l'étape "Shipping & Price" du wizard (voir
  // CJShippingPricingStep.js) : "include" = shipping fondu dans un seul
  // total payé au fournisseur ; "separate" = le shipping est recouvré à
  // part au checkout, donc affiché à part ici aussi plutôt que sommé.
  // Pas encore importé (pas de elakayPricing) -- on retombe sur le
  // comportement bundle par défaut (mode "include" du wizard).
  const supplierCost = hasRealElakayPrice && elakayPricing.supplierCost != null ? Number(elakayPricing.supplierCost) || 0 : cost;
  const supplierShipping = hasRealElakayPrice && elakayPricing.supplierShipping != null ? Number(elakayPricing.supplierShipping) || 0 : shipping;
  const isSeparateShipping = elakayPricing?.shippingMode === "separate";
  const cjBundledTotal = hasRealElakayPrice && elakayPricing.totalSupplierCost != null
    ? Number(elakayPricing.totalSupplierCost) || 0
    : supplierCost + supplierShipping;
  // Empêche le clic sur le switch de déclencher la navigation de la carte
  // (le bloc prix vit dans cjds-card-body, pas dans le Link du titre/image,
  // mais on reste défensif si ce composant est un jour ré-imbriqué).
  const selectSource = (event, next) => { event.preventDefault(); event.stopPropagation(); setSource(next); };
  return h("div", { className: "cjds-price-block" },
    h("div", { className: "cjds-price-switch", role: "tablist", "aria-label": t("h_cj_price_source", "Price source") },
      h("button", {
        type: "button",
        role: "tab",
        "aria-selected": source === "elakay",
        className: `cjds-price-switch-btn${source === "elakay" ? " active" : ""}`,
        onClick: (event) => selectSource(event, "elakay"),
        title: "Elakay",
      }, h("img", { src: elakayLogoIcon, alt: "Elakay" })),
      h("button", {
        type: "button",
        role: "tab",
        "aria-selected": source === "cj",
        className: `cjds-price-switch-btn${source === "cj" ? " active" : ""}`,
        onClick: (event) => selectSource(event, "cj"),
        title: platformName,
      }, platformLogo
        ? h("img", { src: platformLogo, alt: platformName })
        : h("span", { className: "cjds-price-switch-btn--text" }, platformBadgeText))
    ),
    source === "elakay"
      ? h(React.Fragment, null,
          h("div", { className: "cjds-price-row" },
            h("span", { className: "cjds-current-price" }, money(elakayPrice)),
            hasRealElakayPrice && elakayDiscountPercent > 0
              ? h(React.Fragment, null,
                  h("span", { className: "cjds-original-price" }, money(elakayBasePrice)),
                  h("span", { className: "cjds-margin-badge" }, `-${elakayDiscountPercent}%`)
                )
              : (!hasRealElakayPrice && hasMargin ? h("span", { className: "cjds-margin-badge" }, `+${marginPercent}%`) : null)
          ),
          cost > 0 ? h("div", { className: "cjds-price-row cjds-price-row--supplier" },
            h("span", { className: "cjds-muted" }, `${t("h_cj_supplier_cost", "Coût fournisseur")}: ${money(cost)}`)
          ) : null
        )
      : isSeparateShipping
        ? h(React.Fragment, null,
            h("div", { className: "cjds-price-row" },
              h("span", { className: "cjds-current-price" }, money(supplierCost)),
              h("span", { className: "cjds-badge" }, t("h_cj_shipping_separate", "+ livraison séparée"))
            ),
            supplierShipping > 0 ? h("div", { className: "cjds-price-row cjds-price-row--supplier" },
              h("span", { className: "cjds-muted" }, `${t("h_cj_shipping_price", "Livraison")}: ${money(supplierShipping)}`)
            ) : null
          )
        : h(React.Fragment, null,
            h("div", { className: "cjds-price-row" },
              h("span", { className: "cjds-current-price" }, money(cjBundledTotal)),
              supplierShipping > 0 ? h("span", { className: "cjds-badge" }, t("h_cj_shipping_incl", "livraison incl.")) : null
            ),
            h("div", { className: "cjds-price-row cjds-price-row--supplier" },
              h("span", { className: "cjds-muted" }, `${t("h_cj_product_price", "Produit")}: ${money(supplierCost)}`),
              supplierShipping > 0 ? h("span", { className: "cjds-muted" }, `${t("h_cj_shipping_price", "Livraison")}: ${money(supplierShipping)}`) : null
            )
          )
  );
};

const ProductCard = ({ product, t, onImport, onSync, onDelete, onView, busy }) => {
  const location = useLocation();
  const image = product.images?.[0] || "https://placehold.co/600x500/e7f4f6/007399?text=CJ";
  const detailsPath = buildProductDetailsLink(product._id, location);
  return h("article", { className: "cjds-card" },
    h(Link, { to: detailsPath, className: "cjds-product-img-link" },
      h("img", { className: "cjds-product-img", src: image, alt: product.title || "" }),
      onView ? h(IconAction, {
        icon: h(MdVisibility), onClick: () => onView(product), title: t("h_cj_view", "View"),
        className: "cjds-icon-btn--overlay cjds-icon-btn--view",
      }) : null
    ),
    h("div", { className: "cjds-card-body" },
      h(Link, { to: detailsPath, className: "cjds-product-title", title: product.title }, product.title),
      product.supplierName || product.brand ? h("p", { className: "cjds-muted cjds-truncate" }, product.supplierName || product.brand) : null,
      h(ProductCardPrice, { product, t }),
      h("div", { className: "cjds-row cjds-card-meta" },
        h(StatusBadge, { status: product.availability, t }),
        h("span", { className: "cjds-muted" }, `${t("h_cj_stock", "Stock")}: ${product.stockQuantity || 0}`),
        (product.deliveryMinDays || product.deliveryMaxDays) ? h("span", { className: "cjds-muted" },
          `${product.deliveryMinDays ?? "?"}-${product.deliveryMaxDays ?? "?"} ${t("h_cj_days", "days")}`
        ) : null
      ),
      h("div", { className: "cjds-card-actions" },
        onImport && product.importStatus !== "imported"
          ? h(IconAction, {
              icon: h(MdAddShoppingCart), disabled: busy, onClick: () => onImport(product),
              title: t("h_cj_import", "Import to Elakay"), className: "cjds-icon-btn--import",
            })
          : null,
        onSync && product.importStatus === "imported"
          ? h(IconAction, {
              icon: h(MdSync), disabled: busy, onClick: () => onSync(product),
              title: t("h_cj_sync", "Sync"), className: "cjds-icon-btn--sync",
            })
          : null,
        onDelete && product.importStatus === "imported"
          ? h(IconAction, {
              icon: h(MdDeleteForever), disabled: busy, onClick: () => onDelete(product),
              title: t("h_cj_delete_product", "Delete product permanently"), className: "cjds-icon-btn--delete",
            })
          : null
      )
    )
  );
};

const SupplierCard = ({ supplier, t, onView, onSync, busy }) => h("article", { className: "cjds-card cjds-supplier-card" },
  h("div", { className: "cjds-card-body" },
    h("div", { className: "cjds-row cjds-between" },
      h("h3", null, supplier.name),
      h(StatusBadge, { status: supplier.status, t })
    ),
    supplier.country ? h("p", { className: "cjds-muted" }, supplier.country) : null,
    h("p", { className: "cjds-muted" }, `${t("h_cj_products_count", "Products")}: ${supplier.totalProducts || supplier.productCount || 0}`),
    supplier.rating ? h("p", { className: "cjds-muted" }, `★ ${Number(supplier.rating).toFixed(1)}`) : null,
    Array.isArray(supplier.shippingCountries) && supplier.shippingCountries.length
      ? h("p", { className: "cjds-muted" }, `${t("h_cj_ships_to", "Ships to")}: ${supplier.shippingCountries.slice(0, 4).join(", ")}`)
      : null,
    h("div", { className: "cjds-row" },
      onView ? h(ViewButton, { onView: () => onView(supplier), t }) : null,
      onSync ? h(IconAction, {
        icon: h(MdSync), disabled: busy, onClick: () => onSync(supplier),
        title: t("h_cj_sync", "Sync"), className: "cjds-icon-btn--sync",
      }) : null,
      h(LinkButton, { to: `/dropshipping/suppliers/${supplier._id}/products` }, t("h_cj_view_products", "View products"))
    )
  )
);

// `contentTopRef` (ancre posée juste sous le Header Elakay, voir
// CJDropshippingDashboard.jsx) est lu directement ici depuis le contexte
// plutôt que délégué à chaque page appelante -- centralise le scroll fluide
// "retour au début du contenu" pour TOUT usage de PaginationBar (Products,
// Suppliers, ...), sans dupliquer cet appel dans chaque page.
const PaginationBar = ({ page, pageCount, loading, onPrevious, onNext, t }) => {
  const { contentTopRef } = useCJDropshipping();
  // requestAnimationFrame : `onPrevious`/`onNext` déclenchent aussitôt le
  // placeholder "Loading…" (page transition dans ProductCollectionPage.js),
  // qui réduit immédiatement la hauteur de la page. Si le scroll smooth
  // démarre AVANT que ce rétrécissement soit peint, l'animation se fait
  // "rattraper" par le nouveau scrollHeight (plus petit) et le navigateur
  // clamp la position en cours de route -- l'utilisateur reste alors
  // planté près du bas/footer au lieu d'atteindre `contentTopRef`. Attendre
  // la prochaine frame laisse le rétrécissement se peindre d'abord, puis
  // l'animation part d'un layout déjà stable.
  const scrollToContentTop = () => requestAnimationFrame(() => {
    contentTopRef?.current?.scrollIntoView({ behavior: "smooth", block: "start", inline: "nearest" });
  });
  const handlePrevious = () => { scrollToContentTop(); onPrevious(); };
  const handleNext = () => { scrollToContentTop(); onNext(); };
  return h("div", { className: "cjds-pagination" },
    h(Button, { variant: "secondary", disabled: loading || page <= 1, onClick: handlePrevious }, t("h_cj_previous", "Previous")),
    h("span", { className: "cjds-muted" }, t("h_cj_page_indicator", `Page ${page} / ${pageCount}`, { page, pageCount })),
    h(Button, { variant: "secondary", disabled: loading || page >= pageCount, onClick: handleNext }, t("h_cj_next", "Next"))
  );
};

const isEmptyValue = (value) => value === null || value === undefined || value === "";

const formatScalar = (value) => {
  if (isEmptyValue(value)) return "N/A";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const isImageArray = (key, value) =>
  /image/i.test(key) && Array.isArray(value) && value.every((item) => typeof item === "string");

const isObjectArray = (value) => Array.isArray(value) && value.length > 0 && typeof value[0] === "object";

const isDescriptionKey = (key) => /^description$/i.test(key);

const renderKeyValueRow = (key, value, t) => {
  if (isEmptyValue(value)) return t("h_cj_na", "N/A");
  if (isDescriptionKey(key) && typeof value === "string") {
    return h("div", { className: "cjds-text-description" }, value);
  }
  if (isImageArray(key, value)) {
    return h("div", { className: "cjds-kv-images" }, ...value.slice(0, 8).map((url) => h("a", {
      key: url, href: url, target: "_blank", rel: "noreferrer", className: "cjds-kv-image-link",
    }, h("img", { src: url, alt: "", className: "cjds-kv-image" }), h("span", null, url))));
  }
  if (isObjectArray(value)) {
    const columns = Object.keys(value[0]).slice(0, 6);
    return h("table", { className: "cjds-kv-subtable" },
      h("thead", null, h("tr", null, ...columns.map((col) => h("th", { key: col }, col)))),
      h("tbody", null, ...value.map((row, index) => h("tr", { key: index },
        ...columns.map((col) => h("td", { key: col }, formatScalar(row[col])))
      )))
    );
  }
  if (Array.isArray(value)) {
    return value.length ? h("code", { className: "cjds-kv-json" }, JSON.stringify(value)) : t("h_cj_na", "N/A");
  }
  if (typeof value === "object") {
    return h("table", { className: "cjds-kv-subtable" }, h("tbody", null,
      ...Object.entries(value).map(([key2, value2]) => h("tr", { key: key2 },
        h("td", null, key2),
        h("td", null, formatScalar(value2))
      ))
    ));
  }
  return String(value);
};

// Table Key/Value générique pour le tab "Info" du modal de vue CJ : valeurs
// simples telles quelles, tableaux en liste/JSON compact (ou mini-tableau
// pour un tableau d'objets, ex. variants), objets imbriqués en sous-table,
// vide/null/undefined -> N/A, tableaux d'URLs d'images -> preview + URL.
const KeyValueTable = ({ data, t, excludeKeys = [] }) => h("table", { className: "cjds-kv-table" },
  h("tbody", null, ...Object.entries(data || {})
    .filter(([key]) => !excludeKeys.includes(key))
    .map(([key, value]) => h("tr", { key },
      h("td", { className: "cjds-kv-key" }, key),
      h("td", { className: "cjds-kv-value" }, renderKeyValueRow(key, value, t))
    )))
);

const Kpi = ({ value, label }) => h("div", { className: "cjds-kpi" },
  h("strong", null, value),
  h("span", { className: "cjds-muted" }, label)
);

module.exports = {
  PageHeader,
  Button,
  LinkButton,
  ViewButton,
  IconAction,
  StatusBadge,
  ResourceState,
  ProductCard,
  SupplierCard,
  PaginationBar,
  KeyValueTable,
  Kpi,
  HighlightedJSON,
  downloadJSON,
};
